
import { Component, Vue, Watch, Prop } from 'vue-property-decorator';
import { FlowConfig, FieldDefinition, PayloadLimits } from '@/models/flow_config';
import shopifyFlowService from '@/services/shopify-flow.service';

interface TriggerOption {
  label: string;
  value: string;
  description: string;
}

@Component({})
export default class SchedulerOutputFlow extends Vue {
  @Prop({ default: () => ({}) })
  public value!: FlowConfig;

  @Prop({ default: () => ({}) })
  public initConfig!: FlowConfig;

  /**
   * Trigger handles that support typed items array and dynamic field selection.
   * Add new specialized triggers here - used by isSpecializedTrigger getter.
   */
  private static readonly SPECIALIZED_TRIGGERS = new Set([
    'assisty-low-stock-report',
    'assisty-reorder-report'
  ]);

  public flowConfig: FlowConfig = {
    onlyWithData: true,
    minItems: 1,
    maxItems: 50,
    triggerHandle: 'assisty-scheduled-report',
    selectedFields: []
  };

  public availableFields: FieldDefinition[] = [];
  public payloadLimits: PayloadLimits | null = null;
  public fieldsLoading: boolean = false;
  public fieldsError: string = '';

  public triggerOptions: TriggerOption[] = [
    {
      label: 'General Scheduled Report',
      value: 'assisty-scheduled-report',
      description: 'Generic trigger for any report type (max 50 items)'
    },
    {
      label: 'Low Stock Products',
      value: 'assisty-low-stock-report',
      description: 'Typed items array for Flow iteration (dynamic max)'
    },
    {
      label: 'Reorder Products',
      value: 'assisty-reorder-report',
      description: 'Products needing reorder with supplier and quantity info'
    }
  ];

  public get isSpecializedTrigger(): boolean {
    return SchedulerOutputFlow.SPECIALIZED_TRIGGERS.has(this.flowConfig.triggerHandle);
  }

  public get maxItemsLimit(): number {
    if (this.isSpecializedTrigger) {
      return this.calculatedMaxItems;
    }
    return 50;
  }

  public get calculatedMaxItems(): number {
    if (!this.payloadLimits || !this.isSpecializedTrigger) {
      return 50;
    }

    const { baseNullSize, overheadSize, maxPayload, hardCap } = this.payloadLimits;

    let itemSize = 0;
    for (const field of this.availableFields) {
      if (this.flowConfig.selectedFields.includes(field.name)) {
        itemSize += field.sizeBytes;
      } else {
        itemSize += baseNullSize;
      }
    }

    if (itemSize === 0) {
      return hardCap;
    }

    const maxItems = Math.floor((maxPayload - overheadSize) / itemSize);
    return Math.max(1, Math.min(maxItems, hardCap));
  }

  public get payloadPreview(): string {
    if (this.flowConfig.triggerHandle === 'assisty-low-stock-report') {
      return this.getLowStockPayloadPreview();
    }
    if (this.flowConfig.triggerHandle === 'assisty-reorder-report') {
      return this.getReorderPayloadPreview();
    }
    return this.getGenericPayloadPreview();
  }

  public isFieldSelected(fieldName: string): boolean {
    return this.flowConfig.selectedFields.includes(fieldName);
  }

  public toggleField(field: FieldDefinition): void {
    if (field.required) return;

    const index = this.flowConfig.selectedFields.indexOf(field.name);
    if (index > -1) {
      this.flowConfig.selectedFields.splice(index, 1);
    } else {
      this.flowConfig.selectedFields.push(field.name);
    }
  }

  public async fetchFieldDefinitions(triggerHandle: string): Promise<void> {
    if (triggerHandle !== 'assisty-low-stock-report' && triggerHandle !== 'assisty-reorder-report') {
      this.availableFields = [];
      this.payloadLimits = null;
      return;
    }

    this.fieldsLoading = true;
    this.fieldsError = '';

    try {
      const response = await shopifyFlowService.getFieldDefinitions(triggerHandle);
      if (response.success) {
        this.availableFields = response.data.fields;
        this.payloadLimits = response.data.payloadLimits;

        // Set default selected fields if not already set
        if (!this.flowConfig.selectedFields || this.flowConfig.selectedFields.length === 0) {
          this.flowConfig.selectedFields = this.availableFields
            .filter(f => f.default)
            .map(f => f.name);
        }
      } else {
        this.fieldsError = response.error || 'Failed to load field definitions';
      }
    } catch (error: any) {
      console.error('Failed to fetch field definitions:', error);
      this.fieldsError = error.message || 'Failed to load field definitions';
    } finally {
      this.fieldsLoading = false;
    }
  }

  private getLowStockPayloadPreview(): string {
    const sampleItems = this.availableFields.length > 0
      ? [
          this.buildSampleItem({
            variantId: '789012',
            variantTitle: 'Black',
            sku: 'SKU-001',
            barcode: '123456789',
            inventoryItemId: '456789',
            productId: '123456',
            productTitle: 'Premium Wireless Headphones',
            vendor: 'AudioTech Inc',
            availableQty: 3,
            daysOfInventory: 5,
            unitCost: 45.00
          }),
          this.buildSampleItem({
            variantId: '789013',
            variantTitle: 'Blue / Large',
            sku: 'SKU-002',
            barcode: '987654321',
            inventoryItemId: '456790',
            productId: '123457',
            productTitle: 'Bluetooth Speaker',
            vendor: 'SoundWave',
            availableQty: 7,
            daysOfInventory: 12,
            unitCost: 32.50
          })
        ]
      : [];

    const totalItems = 25;
    const maxItems = this.calculatedMaxItems;
    const isTruncated = totalItems > maxItems;

    const example = {
      triggerId: 'evt_lowstockreport_123_1702656000000',
      timestamp: new Date().toISOString(),
      lowStockReport: {
        id: '123',
        title: 'Low Stock Alert',
        interval: 'daily',
        timezone: 'America/New_York',
        reportConfig: {
          name: 'Low Stock Report',
          measureCode: 'low_stock',
          dimensionCode: 'product_variant',
          analyticsType: 'snapshot',
          timeRange: ['7D'],
          filters: [
            { field: 'quantity', operator: 'lt', value: '10', dataType: 'number' }
          ],
          parameters: [
            { code: 'topn', value: '100' }
          ]
        },
        totalItems: totalItems,
        itemsIncluded: Math.min(totalItems, maxItems),
        itemsTruncated: isTruncated,
        downloadLink: isTruncated ? 'https://app.assisty.ai/download/report-123.csv' : '',
        store: {
          shopDomain: 'your-store.myshopify.com',
          timezone: 'America/New_York'
        },
        items: sampleItems,
        formattedDateRange: 'Dec 11 - Dec 18, 2025',
        topItemsPreview: '*SKU | Product | Variant | Quantity*\n1. SKU-001 | Premium Wireless Headphones | Black | 3\n2. SKU-002 | Bluetooth Speaker | Blue / Large | 7',
        summaryMessage: `📊 *Scheduled Report: Low Stock Alert*

*Report:* Low Stock Report
*Interval:* daily

━━━━━━━━━━━━━━━━━━━━━━
📈 *Summary*
• Total Items: ${totalItems}

📋 *Top 5 Items:*
*SKU | Product | Variant | Quantity*
1. SKU-001 | Premium Wireless Headphones | Black | 3
2. SKU-002 | Bluetooth Speaker | Blue / Large | 7

━━━━━━━━━━━━━━━━━━━━━━
📥 *Download Full Report:*
<https://app.assisty.ai/download/report-123.csv|report-123.csv>`
      }
    };
    return JSON.stringify(example, null, 2);
  }

  private getReorderPayloadPreview(): string {
    const sampleItems = this.availableFields.length > 0
      ? [
          this.buildSampleItem({
            variantId: '789012',
            variantTitle: 'Black',
            sku: 'SKU-001',
            barcode: '123456789',
            inventoryItemId: '456789',
            productId: '123456',
            productTitle: 'Premium Wireless Headphones',
            vendor: 'AudioTech Inc',
            availableQty: 3,
            daysOfInventory: 5,
            forecastDailySales: 7.66,
            unitCost: 45.00,
            reorderQty: 50,
            totalCost: 2250.00,
            supplierName: 'Global Electronics Ltd',
            leadTime: 14,
            reorderDate: '2025-01-10',
            safetyStock: 10,
            reorderPoint: 25
          }),
          this.buildSampleItem({
            variantId: '789013',
            variantTitle: 'Blue / Large',
            sku: 'SKU-002',
            barcode: '987654321',
            inventoryItemId: '456790',
            productId: '123457',
            productTitle: 'Bluetooth Speaker',
            vendor: 'SoundWave',
            availableQty: 7,
            daysOfInventory: 12,
            forecastDailySales: 2.74,
            unitCost: 32.50,
            reorderQty: 30,
            totalCost: 975.00,
            supplierName: 'Audio Supplies Inc',
            leadTime: 7,
            reorderDate: '2025-01-03',
            safetyStock: 5,
            reorderPoint: 15
          })
        ]
      : [];

    const totalItems = 18;
    const maxItems = this.calculatedMaxItems;
    const isTruncated = totalItems > maxItems;

    const example = {
      triggerId: 'evt_reorderreport_123_1702656000000',
      timestamp: new Date().toISOString(),
      reorderReport: {
        id: '123',
        title: 'Weekly Reorder Report',
        interval: 'weekly',
        timezone: 'America/New_York',
        reportConfig: {
          name: 'Reorder Report',
          measureCode: 'reorder',
          dimensionCode: 'product_variant',
          analyticsType: 'snapshot',
          timeRange: ['7D'],
          filters: [],
          parameters: [
            { code: 'topn', value: '100' }
          ]
        },
        totalItems: totalItems,
        itemsIncluded: Math.min(totalItems, maxItems),
        itemsTruncated: isTruncated,
        downloadLink: isTruncated ? 'https://app.assisty.ai/download/report-123.csv' : '',
        totalReorderValue: 3225.00,
        totalReorderQuantity: 80,
        store: {
          shopDomain: 'your-store.myshopify.com',
          timezone: 'America/New_York'
        },
        items: sampleItems,
        formattedDateRange: 'Dec 11 - Dec 18, 2025',
        topItemsPreview: '*SKU | Product | Supplier | Qty | Cost*\n1. SKU-001 | Premium Wireless Headphones | Global Electronics Ltd | 50 | $2,250.00\n2. SKU-002 | Bluetooth Speaker | Audio Supplies Inc | 30 | $975.00',
        summaryMessage: `📊 *Scheduled Report: Weekly Reorder Report*

*Report:* Reorder Report
*Interval:* weekly

━━━━━━━━━━━━━━━━━━━━━━
📈 *Summary*
• Total Items: ${totalItems}
• Total Reorder Value: $3,225.00
• Total Reorder Quantity: 80

📋 *Top 5 Items:*
*SKU | Product | Supplier | Qty | Cost*
1. SKU-001 | Premium Wireless Headphones | Global Electronics Ltd | 50 | $2,250.00
2. SKU-002 | Bluetooth Speaker | Audio Supplies Inc | 30 | $975.00

━━━━━━━━━━━━━━━━━━━━━━
📥 *Download Full Report:*
<https://app.assisty.ai/download/report-123.csv|report-123.csv>`
      }
    };
    return JSON.stringify(example, null, 2);
  }

  private buildSampleItem(fullItem: Record<string, any>): Record<string, any> {
    const result: Record<string, any> = {};
    for (const field of this.availableFields) {
      if (this.flowConfig.selectedFields.includes(field.name)) {
        result[field.name] = fullItem[field.name] ?? null;
      } else {
        result[field.name] = null;
      }
    }
    return result;
  }

  private getGenericPayloadPreview(): string {
    const sampleItems = [
      {
        rank: 1,
        sku: 'SKU-001',
        productTitle: 'Premium Wireless Headphones',
        variantTitle: 'Black',
        quantity: 5,
        reorderPoint: 20,
        productGid: 'gid://shopify/Product/123456',
        variantGid: 'gid://shopify/ProductVariant/789012',
        locationGid: 'gid://shopify/Location/456789'
      },
      {
        rank: 2,
        sku: 'SKU-002',
        productTitle: 'Bluetooth Speaker',
        variantTitle: 'Blue / Large',
        quantity: 3,
        reorderPoint: 15,
        productGid: 'gid://shopify/Product/123457',
        variantGid: 'gid://shopify/ProductVariant/789013'
      }
    ];

    const totalItems = 25;
    const isTruncated = totalItems > this.flowConfig.maxItems;

    const example = {
      triggerId: 'evt_scheduledreport_123_1702656000000',
      timestamp: new Date().toISOString(),
      scheduledReport: {
        id: '123',
        title: 'Low Stock Alert',
        interval: 'daily',
        timezone: 'America/New_York',
        reportConfig: {
          name: 'Low Stock Report',
          measureCode: 'low_stock',
          dimensionCode: 'product_variant',
          analyticsType: 'snapshot',
          timeRange: ['7D'],
          filters: [
            { field: 'quantity', operator: 'lt', value: '10', dataType: 'number' }
          ],
          parameters: [
            { code: 'topn', value: '100' }
          ]
        },
        totalItems: totalItems,
        itemsIncluded: Math.min(totalItems, this.flowConfig.maxItems),
        itemsTruncated: isTruncated,
        downloadLink: isTruncated ? 'https://app.assisty.ai/download/report-123.csv' : '',
        store: {
          shopDomain: 'your-store.myshopify.com',
          timezone: 'America/New_York'
        },
        itemsJson: JSON.stringify(sampleItems.slice(0, this.flowConfig.maxItems)),
        formattedDateRange: 'Dec 11 - Dec 18, 2025',
        topItemsPreview: '*SKU | Product | Variant | Quantity*\n1. SKU-001 | Premium Wireless Headphones | Black | 5\n2. SKU-002 | Bluetooth Speaker | Blue / Large | 3',
        summaryMessage: `📊 *Scheduled Report: Low Stock Alert*

*Report:* Low Stock Report
*Interval:* daily

━━━━━━━━━━━━━━━━━━━━━━
📈 *Summary*
• Date Range: Dec 11 - Dec 18, 2025
• Total Items: ${totalItems}

📋 *Top 5 Items:*
*SKU | Product | Variant | Quantity*
1. SKU-001 | Premium Wireless Headphones | Black | 5
2. SKU-002 | Bluetooth Speaker | Blue / Large | 3

━━━━━━━━━━━━━━━━━━━━━━
📥 *Download Full Report:*
<https://app.assisty.ai/download/report-123.csv|report-123.csv>`
      }
    };
    return JSON.stringify(example, null, 2);
  }

  @Watch('flowConfig.triggerHandle', { immediate: true })
  private onTriggerHandleChanged(newVal: string): void {
    this.fetchFieldDefinitions(newVal);
  }

  @Watch('flowConfig', { immediate: true, deep: true })
  private onConfigChanged(): void {
    // Only clamp maxItems for the generic trigger (which has a hard limit of 50)
    // For specialized triggers (low-stock, reorder), the limit is dynamic based on selected fields
    // and payloadLimits (which may not be loaded yet during initialization)
    if (!this.isSpecializedTrigger) {
      const maxLimit = 50;
      if (this.flowConfig.maxItems > maxLimit) {
        this.flowConfig.maxItems = maxLimit;
      }
    }
    if (this.flowConfig.maxItems < 1) {
      this.flowConfig.maxItems = 1;
    }
    // Ensure minItems is at least 1
    if (this.flowConfig.minItems < 1) {
      this.flowConfig.minItems = 1;
    }
    // Cross-field validation: minItems cannot exceed maxItems
    if (this.flowConfig.minItems > this.flowConfig.maxItems) {
      this.flowConfig.minItems = this.flowConfig.maxItems;
    }
    this.$emit('input', this.flowConfig);
  }

  @Watch('initConfig', { immediate: true })
  private onInitConfigChanged(newVal: FlowConfig): void {
    if (newVal && Object.keys(newVal).length > 0) {
      // Merge initConfig values, ensuring they override defaults
      // Use direct assignment to avoid triggering unnecessary reactivity
      if (newVal.onlyWithData !== undefined) this.flowConfig.onlyWithData = newVal.onlyWithData;
      if (newVal.minItems !== undefined) this.flowConfig.minItems = newVal.minItems;
      if (newVal.maxItems !== undefined) this.flowConfig.maxItems = newVal.maxItems;
      if (newVal.triggerHandle !== undefined) this.flowConfig.triggerHandle = newVal.triggerHandle;
      if (newVal.selectedFields !== undefined) this.flowConfig.selectedFields = [...newVal.selectedFields];
    }
  }
}
